import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { DndProvider, getBackendOptions, MultiBackend } from '@minoru/react-dnd-treeview'
import { layoutStore, SpinnerLoader } from 'shared/ui'
import {
  type IDropOptions,
  type IInboxTreeCombine,
  type INodeModel,
  TreeRootInboxes,
} from 'entities/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useConversationMenuContext } from 'pages/conversations/ui/ConversationMenu/context'
import { actionTeamsFavoriteDragDrop } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/Dropdown/Actions/TeamsFavoriteDragDrop'
import { actionMoveToDragDrop } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/Dropdown/Actions/MoveToDragDrop'
import { MenuSectionSmartViewsTree } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuSectionSmartViewsTree'
import { MenuSectionTree } from './MenuSectionTree/MenuSectionTree'
import { RequestAccessItem } from './RequestAccessItem/RequestAccessItem'
import { MenuQuickFilters } from './MenuQuickFilters/MenuQuickFilters'
import styles from './styles.module.scss'

export const ConversationMenuInboxes = observer(() => {
  const store = useConversationMenuContext()
  const { usersStore } = useEntitiesContext()
  const {
    selectInboxesFavoritesCount,
    selectInboxesSimpleCount,
    searchFavorite,
    searchSimple,
    loading,
    itemsFavorites,
    itemsInboxes,
    handleUpdateSearchSimple,
    handleUpdateSearchFavorite,
    pageLayoutStore,
  } = store
  const isShouldRequestAccess = !!usersStore.user?.isShouldRequestAccess

  const handleDrop = async (
    items: INodeModel<IInboxTreeCombine>[],
    options: IDropOptions<IInboxTreeCombine>,
    dropRoot: TreeRootInboxes
  ) => {
    await actionTeamsFavoriteDragDrop(options, dropRoot, store)
    await actionMoveToDragDrop(options, dropRoot, store)
  }
  const itemsFavoritesCount = selectInboxesFavoritesCount
  const itemsInboxesCount = selectInboxesSimpleCount

  const hideFavoritesInboxes = itemsFavorites.length === 0 && searchFavorite === ''
  const hideSimpleInboxes = itemsInboxes.length === 0 && searchSimple === ''

  useEffect(() => {
    if (isShouldRequestAccess) {
      store.setLoadingUser(false)
    }
  }, [isShouldRequestAccess])

  if (loading) {
    return (
      <div className={classnames(styles.loader)}>
        <SpinnerLoader ariaLabel={'ConversationMenuInboxes'} />
      </div>
    )
  }

  if (isShouldRequestAccess) return <RequestAccessItem />

  const onClickItemTree = () => {
    if (layoutStore.isMediumView) {
      pageLayoutStore.handleChangeCollapse(true)
    }
  }

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <MenuQuickFilters />
      <div className={styles.wrap}>
        {!hideFavoritesInboxes && (
          <MenuSectionTree
            items={itemsFavorites}
            count={itemsFavoritesCount}
            title='Favorites'
            root={TreeRootInboxes.FAVORITE}
            useSearch={{
              handleSearch: handleUpdateSearchFavorite,
              search: searchFavorite,
              placeholder: 'Search favorites',
              autofocus: true,
              variant: 'transparent',
            }}
            handleDrop={handleDrop}
            onClickItemTree={onClickItemTree}
          />
        )}

        <MenuSectionSmartViewsTree root={TreeRootInboxes.SMART_VIEW} handleDrop={handleDrop} />

        {!hideSimpleInboxes && (
          <MenuSectionTree
            items={itemsInboxes}
            count={itemsInboxesCount}
            title='Inboxes'
            root={TreeRootInboxes.SIMPLE}
            handleDrop={handleDrop}
            onClickItemTree={onClickItemTree}
            useSearch={{
              handleSearch: handleUpdateSearchSimple,
              search: searchSimple,
              placeholder: 'Search inboxes',
              autofocus: true,
              variant: 'transparent',
            }}
          />
        )}
      </div>
    </DndProvider>
  )
})
